import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { AddItemModal } from '../components/AddItemModal';
import { useAuth } from '../contexts/AuthContext';
import { Package, AlertTriangle, Clock, Plus, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HTTP_UNAUTHORIZED = 401;

// --- Sub-components ---

const StatCard = ({ label, value, icon: Icon, colorClass, bgClass, delay = '0s', testId }) => (
  <div className="card animate-fade-in" style={{ animationDelay: delay }} data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-[#475569] font-bold mb-1">{label}</p>
        <p className={`font-heading text-4xl font-bold ${colorClass}`}>{value}</p>
      </div>
      <div className={`w-14 h-14 ${bgClass} flex items-center justify-center`}>
        <Icon className={`w-7 h-7 ${colorClass}`} />
      </div>
    </div>
  </div>
);

const AlertList = ({ title, icon: Icon, iconColor, items, emptyText, renderItem, testId }) => (
  <div className="card" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-5 h-5 ${iconColor}`} />
      <h2 className="font-heading text-xl font-bold text-[#0A192F]">{title}</h2>
    </div>
    {items.length === 0 ? (
      <p className="text-[#475569] text-sm font-body py-4">{emptyText}</p>
    ) : (
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {items.slice(0, 5).map(renderItem)}
      </div>
    )}
  </div>
);

// --- Main component ---

export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_items: 0,
    low_stock_count: 0,
    expiring_count: 0,
    category_counts: {}
  });
  const [alerts, setAlerts] = useState({ low_stock: [], expiring_soon: [] });
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory/alerts`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddItem = async (itemData) => {
    try {
      await axios.post(`${API_URL}/api/inventory`, itemData, { withCredentials: true });
      toast.success('Item added successfully');
      fetchData();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A192F]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      <Sidebar />
      
      <main className="flex-1 p-8" data-testid="dashboard-main">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#0A192F]">Dashboard</h1>
            <p className="text-[#475569] font-body mt-1">Welcome back, {user?.name}</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#0A192F] text-white hover:bg-[#0F294D] uppercase tracking-wide text-sm font-bold"
            data-testid="quick-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Quick Add
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            label="Total Items" value={stats.total_items}
            icon={Package} colorClass="text-[#0A192F]" bgClass="bg-slate-100"
            testId="total-items-card"
          />
          <StatCard
            label="Low Stock" value={stats.low_stock_count}
            icon={TrendingDown} colorClass="text-red-600" bgClass="bg-red-50"
            delay="0.1s" testId="low-stock-card"
          />
          <StatCard
            label="Expiring Soon" value={stats.expiring_count}
            icon={Clock} colorClass="text-amber-600" bgClass="bg-amber-50"
            delay="0.2s" testId="expiring-card"
          />
        </div>

        {/* Alerts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AlertList
            title="Low Stock Items" icon={AlertTriangle} iconColor="text-red-600"
            items={alerts.low_stock} emptyText="No low stock alerts"
            testId="low-stock-alerts"
            renderItem={(item) => (
              <div
                key={item.item_id}
                className="flex items-center justify-between p-3 bg-red-50 border border-red-100"
                data-testid={`low-stock-item-${item.item_id}`}
              >
                <div>
                  <p className="font-body font-semibold text-[#0A192F]">{item.name}</p>
                  <p className="text-xs text-[#475569]">{item.category}</p>
                </div>
                <span className="badge-low-stock">{item.quantity} left</span>
              </div>
            )}
          />

          <AlertList
            title="Expiring Soon" icon={Clock} iconColor="text-amber-600"
            items={alerts.expiring_soon} emptyText="No items expiring soon"
            testId="expiring-alerts"
            renderItem={(item) => (
              <div
                key={item.item_id}
                className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100"
                data-testid={`expiring-item-${item.item_id}`}
              >
                <div>
                  <p className="font-body font-semibold text-[#0A192F]">{item.name}</p>
                  <p className="text-xs text-[#475569]">{item.category}</p>
                </div>
                <span className="badge-expiring">
                  {new Date(item.expiry_date).toLocaleDateString()}
                </span>
              </div>
            )}
          />
        </div>

        {/* Category Breakdown */}
        {Object.keys(stats.category_counts).length > 0 && (
          <div className="card mt-6" data-testid="category-breakdown">
            <h2 className="font-heading text-xl font-bold text-[#0A192F] mb-4">Items by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(stats.category_counts).map(([category, count]) => (
                <div key={category} className="text-center p-4 bg-slate-50 border border-slate-200">
                  <p className="font-heading text-2xl font-bold text-[#0A192F]">{count}</p>
                  <p className="text-xs uppercase tracking-wider text-[#475569] font-bold mt-1">{category}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <AddItemModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddItem}
      />
    </div>
  );
};
